<template>
  <footer class="dark-theme py-32 bg-surface-page md:py-64">
    <div class="page-wrapper">
      <FooterNavLinks />

      <div class="flex gap-16 flex-col md:gap-24 lg:flex-row">
        <div class="w-full">
          <h2 class="heading-xxs">Om du har ett ärende</h2>
          <div
            class="flex gap-16 flex-col mt-16 md:mt-24 md:gap-24 md:flex-row"
          >
            <FooterBoxLink
              link="https://www.halebop.se/support"
              heading-label="Kontakta oss"
              text="Chatta med oss 24/7 - Vi finns här för dig!"
            >
              <template #left-icon>
                <nuxt-icon
                  name="chat"
                  aria-hidden="true"
                  class="text-icon-secondary text-[2.4rem]"
                />
              </template>
            </FooterBoxLink>
            <FooterBoxLink
              link="https://www.halebop.se/mitt-halebop"
              heading-label="Logga in"
              text="Hantera ärenden, fakturor och surfmängd."
            >
              <template #left-icon>
                <nuxt-icon
                  name="log-in"
                  aria-hidden="true"
                  class="text-icon-secondary text-[2.4rem]"
                />
              </template>
            </FooterBoxLink>
          </div>
        </div>

        <div class="w-full">
          <h2 class="heading-xxs">Ladda ned våra appar</h2>
          <div
            class="flex gap-16 mt-16 flex-col md:gap-24 md:mt-24 md:flex-row"
          >
            <FooterBoxLink
              link="https://www.halebop.se/locall"
              heading-label="Locall"
              text="Billiga samtal utomlands, utan internet!"
            >
              <template #left-icon>
                <NuxtImg
                  src="/images/logos/locall_logo.svg"
                  alt=""
                  class="w-24 h-24"
                  loading="lazy"
                  provider="cloudfront"
                />
              </template>
            </FooterBoxLink>
            <FooterBoxLink
              link="https://www.halebop.se/halebop-app"
              heading-label="Mitt Halebop"
              text="Hantera ärenden, fakturor och surfmängd."
            >
              <template #left-icon>
                <NuxtImg
                  src="/images/logos/symbol_logo_thumb.png"
                  alt=""
                  class="w-24 h-24"
                  loading="lazy"
                  provider="cloudfront"
                />
              </template>
            </FooterBoxLink>
          </div>
        </div>
      </div>

      <div class="flex gap-32 relative flex-col 2xl:flex-row 2xl:gap-16">
        <div class="w-full">
          <div
            class="flex gap-16 mt-24 md:gap-24 xl:mb-24 flex-col md:flex-row pr-0 max-w-full xl:pr-12 xl:max-w-[50%] 2xl:max-w-660 2xl:pr-0"
          >
            <FooterBoxLinkDrake
              link="https://www.halebop.se/support"
              img-src="/images/sitewide/footer_drake_yes.png"
              img-alt="Ja till chattsupport"
              header-label="Chattsupport"
              text="Snabbt svar och öppet 24/7"
              badge-img-src="/images/sitewide/footer_alert_badge_green.svg"
              badge-img-alt="Ja till chattsupport"
            />
            <FooterBoxLinkDrake
              link="mailto:kontakta-oss@halebop.se?subject=Halebop%20HBP"
              img-src="/images/sitewide/footer_drake_no.png"
              img-alt="Nej till mail"
              header-label="<span>Mail: </span>kontakta-oss@halebop.se"
              text="Långa svarstider"
              badge-img-src="/images/sitewide/footer_alert_badge_red.svg"
              badge-img-alt="Nej till mail"
            />
          </div>
        </div>
      </div>

      <div
        class="flex flex-col md:flex-row md:justify-between mt-32 md:mt-24 lg:mt-16 xl:mt-0"
      >
        <div>
          <ul class="flex flex-col md:flex-row flex-wrap">
            <li
              v-for="link in navLinksSecondary"
              :key="link.url + link.name"
              class="break-inside-avoid-column list-none py-10 me-8"
            >
              <NuxtLink
                :to="link.url"
                external
                class="standalone text-text-link hover:text-text-link-hover body-link-md"
              >
                {{ link.name }}
              </NuxtLink>
            </li>
          </ul>
          <p class="text-18-22 mt-32 md:mt-24 mb-16">Betalningsalternativ</p>
          <PaymentLogosHorizontal />
        </div>

        <div class="flex flex-col items-start md:items-end mt-32 md:mt-0">
          <NuxtImg
            src="/images/logos/halebop_logo_blue.svg"
            alt="Halebop logga i blå färg"
            class="w-168 mb-16 md:mb-24"
            width="168"
            loading="lazy"
            provider="cloudfront"
          />

          <div class="flex flex-col">
            <p
              class="flex flex-col lg:flex-row lg:justify-end flex-nowrap mb-0 md:mb-8 text-start md:text-end"
            >
              <span class="whitespace-nowrap me-4">
                © Telia Sverige AB 556430-0142
              </span>
              <span class="whitespace-nowrap"> Säte: Stockholm </span>
            </p>
            <p
              class="flex flex-col lg:flex-row flex-nowrap text-start md:text-end"
            >
              <span class="whitespace-nowrap me-0 lg:me-4">
                Vill du bli uppringd av vår kundtjänst:
              </span>
              <a
                href="tel:+46 134 95 07 84"
                class="text-text-primary whitespace-nowrap"
                aria-label="Telefonnummer +46 134 95 07 84"
              >
                +46 134 95 07 84
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
const { functionalConsented } = useCookieConsent();

const navLinksSecondary = [
  {
    name: 'Täckningskarta',
    url: 'https://www.halebop.se/tackningskarta',
  },
  {
    name: 'Bredband driftinformation',
    url: 'https://www.halebop.se/driftstorningar',
  },
  {
    name: 'Priser & Villkor',
    url: 'https://www.halebop.se/villkor',
  },
  {
    name: 'Utlandspriser',
    url: 'https://www.halebop.se/utland',
  },
  {
    name: 'Cookies',
    url: 'https://www.halebop.se/cookie-consent',
  },
  {
    name: 'Integritetspolicy',
    url: 'https://www.halebop.se/villkor-och-prislistor/integritetspolicy',
  },
  {
    name: 'Tillgänglighet',
    url: 'https://www.halebop.se/om-halebop/tillganglighet',
  },
];
</script>

<style scoped lang="scss"></style>
